// app/features/ai-workflows/components/PersonalizedUseCaseCardComponent.tsx
// v2

// app/features/personalized-use-cases/components/PersonalizedUseCaseCardComponent.tsx
"use client";

import {
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Loader2,
  RotateCcw,
  Sparkles,
  XCircle,
  Zap,
} from "lucide-react";
import Link from "next/link";
import React from "react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { type Database } from "@/types/supabase";

import { usePersonalizedUseCasesHook } from "../services_and_hooks/usePersonalizedUseCasesHook";
import { RestartUseCaseButtonComponent } from "./RestartUseCaseButtonComponent";
import { UseCaseActionsDropdownComponent } from "./UseCaseActionsDropdownComponent";

type PersonalizedUseCase =
  Database["public"]["Tables"]["personalized_use_cases"]["Row"];

// 🔥 IMPROVED: Complete status configuration including all possible statuses
const statusConfig = {
  generated: {
    label: "Ready",
    bgColor: "bg-green-500/10",
    textColor: "text-green-600",
    borderColor: "border-green-500/20",
    icon: CheckCircle,
    iconColor: "text-green-500",
    priority: 1,
  },
  completed: {
    // 🔥 CRITICAL FIX: Added 'completed' status
    label: "Ready",
    bgColor: "bg-green-500/10",
    textColor: "text-green-600",
    borderColor: "border-green-500/20",
    icon: CheckCircle,
    iconColor: "text-green-500",
    priority: 1,
  },
  pending: {
    label: "Starting...",
    bgColor: "bg-blue-500/10",
    textColor: "text-blue-600",
    borderColor: "border-blue-500/20",
    icon: Clock,
    iconColor: "text-blue-500",
    priority: 2,
  },
  enhancing_problem: {
    label: "Analyzing Problem",
    bgColor: "bg-purple-500/10",
    textColor: "text-purple-600",
    borderColor: "border-purple-500/20",
    icon: Loader2,
    iconColor: "text-purple-500",
    priority: 3,
  },
  generating_workflows: {
    label: "Creating Workflows",
    bgColor: "bg-purple-500/10",
    textColor: "text-purple-600",
    borderColor: "border-purple-500/20",
    icon: Loader2,
    iconColor: "text-purple-500",
    priority: 4,
  },
  generating_content: {
    label: "Adding Details",
    bgColor: "bg-indigo-500/10",
    textColor: "text-indigo-600",
    borderColor: "border-indigo-500/20",
    icon: Loader2,
    iconColor: "text-indigo-500",
    priority: 5,
  },
  assembling: {
    label: "Finalizing",
    bgColor: "bg-yellow-500/10",
    textColor: "text-yellow-700",
    borderColor: "border-yellow-500/20",
    icon: Loader2,
    iconColor: "text-yellow-600",
    priority: 6,
  },
  generating_full_workflows: {
    label: "Building Workflows",
    bgColor: "bg-indigo-500/10",
    textColor: "text-indigo-600",
    borderColor: "border-indigo-500/20",
    icon: Loader2,
    iconColor: "text-indigo-500",
    priority: 7,
  },
  regenerating: {
    label: "Regenerating",
    bgColor: "bg-orange-500/10",
    textColor: "text-orange-600",
    borderColor: "border-orange-500/20",
    icon: RotateCcw,
    iconColor: "text-orange-500",
    priority: 8,
  },
  failed: {
    label: "Failed",
    bgColor: "bg-red-500/10",
    textColor: "text-red-600",
    borderColor: "border-red-500/20",
    icon: XCircle,
    iconColor: "text-red-500",
    priority: 9,
  },
  // 🔥 NEW: Add fallback statuses that might appear
  generating_problem: {
    label: "Processing Problem",
    bgColor: "bg-purple-500/10",
    textColor: "text-purple-600",
    borderColor: "border-purple-500/20",
    icon: Loader2,
    iconColor: "text-purple-500",
    priority: 3,
  },
  conceptual_workflows_generated: {
    label: "Finalizing Workflows",
    bgColor: "bg-indigo-500/10",
    textColor: "text-indigo-600",
    borderColor: "border-indigo-500/20",
    icon: Loader2,
    iconColor: "text-indigo-500",
    priority: 7,
  },
} as const;

type StatusKey = keyof typeof statusConfig;

// 🔥 IMPROVED: Better status config getter with explicit handling of all statuses
const getStatusConfig = (status: string | null) => {
  // Handle null or undefined status
  if (!status) {
    return statusConfig.pending;
  }

  // Handle completed status - treat as generated/ready
  if (status === "completed" || status === "generated") {
    return statusConfig.generated; // Both use the same "Ready" config
  }

  // Handle other known statuses
  if (status in statusConfig) {
    return statusConfig[status as StatusKey];
  }

  // 🔥 IMPROVED: Default fallback for unknown statuses - treat as generating
  return {
    label: "Processing...",
    bgColor: "bg-blue-500/10",
    textColor: "text-blue-600",
    borderColor: "border-blue-500/20",
    icon: Loader2,
    iconColor: "text-blue-500",
    priority: 5,
  };
};

interface PersonalizedUseCaseCardProps {
  useCase: PersonalizedUseCase;
  className?: string;
}

export function PersonalizedUseCaseCardComponent({
  useCase,
  className,
}: PersonalizedUseCaseCardProps) {
  const { deleteUseCase, isDeletingUseCase } = usePersonalizedUseCasesHook();

  const statusConf = getStatusConfig(useCase.generation_status);
  const StatusIcon = statusConf.icon;
  const isGenerating = statusConf.priority > 1 && statusConf.priority < 9;
  const hasTitle = Boolean(useCase.title);
  const isFailed = useCase.generation_status === "failed";

  // 🔥 IMPROVED: More granular completion check
  const isCompleted =
    useCase.generation_status === "completed" ||
    useCase.generation_status === "generated";
  const isReady = isCompleted && hasTitle;

  // 🔥 CRITICAL: Allow clicking for all non-failed use cases, not just ready ones
  const isClickable = !isFailed;

  // console.log('[PersonalizedUseCaseCardComponent] Rendering card:', {
  //   id: useCase.id,
  //   generation_status: useCase.generation_status,
  //   hasTitle,
  //   isCompleted,
  //   isReady,
  //   isClickable,
  //   isFailed,
  //   isGenerating,
  //   statusLabel: statusConf.label
  // });

  const getRelativeTime = (dateString: string | null | undefined) => {
    if (!dateString) return "Unknown";
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

      if (diffInSeconds < 60) return "Just now";
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays}d ago`;
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Unknown";
    }
  };

  // Card content component
  const CardContent = () => (
    <div
      className={cn(
        "bg-beige-light rounded-xl p-6 transition-all duration-300 ease-in-out",
        "shadow-sm",
        // Only add hover effects for clickable (non-failed) cards
        isClickable && "hover:shadow-xl focus-within:shadow-xl",
        "h-full flex flex-col",
        !isClickable && "cursor-default",
      )}
    >
      {/* Header with status */}
      <div className="flex items-start justify-between mb-4">
        <Badge
          variant="outline"
          className={cn(
            "text-xs font-semibold py-1 px-2.5 rounded-full flex items-center gap-1.5",
            statusConf.bgColor,
            statusConf.textColor,
            statusConf.borderColor.replace("border-", "border-2 border-"),
          )}
        >
          <StatusIcon
            size={14}
            className={cn(isGenerating && "animate-spin")}
          />
          {statusConf.label}
        </Badge>
        <div className="flex items-center gap-2">
          <div className="text-xs text-gray-500 flex items-center gap-1.5 pt-1">
            <Calendar size={13} />
            {getRelativeTime(useCase.created_at)}
          </div>
          <div
            className="pt-1"
            onClick={(e) => e.stopPropagation()} // Prevent card click when interacting with dropdown
          >
            <UseCaseActionsDropdownComponent
              useCase={useCase}
              onDelete={deleteUseCase}
              isDeleting={isDeletingUseCase}
            />
          </div>
        </div>
      </div>

      {/* Title - Show for all non-failed use cases */}
      {!isFailed && (
        <h3
          className={cn(
            "font-semibold text-gray-800 mb-3 line-clamp-2 min-h-[2.5em]",
            hasTitle ? "text-xl" : "text-lg text-gray-500 italic",
          )}
        >
          {hasTitle
            ? useCase.title
            : isGenerating
              ? "Generating use case..."
              : "Use Case"}
        </h3>
      )}

      {/* Content sections - flex-grow to push footer down, with max height to prevent huge cards */}
      <div className="flex-grow space-y-4 mb-5 max-h-40 overflow-hidden">
        {useCase.user_input_problem_statement && (
          <div>
            <div className="flex items-center gap-2 mb-1.5 text-gray-500">
              <FileText size={14} />
              <span className="text-xs font-medium uppercase tracking-wider">
                Problem
              </span>
            </div>
            <p className="text-sm text-gray-700 line-clamp-3">
              {useCase.user_input_problem_statement}
            </p>
          </div>
        )}

        {useCase.user_input_desired_outcome && (
          <div>
            <div className="flex items-center gap-2 mb-1.5 text-gray-500">
              <Zap size={14} />
              <span className="text-xs font-medium uppercase tracking-wider">
                Desired Outcome
              </span>
            </div>
            <p className="text-sm text-gray-700 line-clamp-3">
              {useCase.user_input_desired_outcome}
            </p>
          </div>
        )}

        {useCase.user_input_additional_context &&
          !useCase.user_input_desired_outcome && (
            <div>
              <div className="flex items-center gap-2 mb-1.5 text-gray-500">
                <Sparkles size={14} />
                <span className="text-xs font-medium uppercase tracking-wider">
                  Context
                </span>
              </div>
              <p className="text-sm text-gray-700 line-clamp-2">
                {useCase.user_input_additional_context}
              </p>
            </div>
          )}
      </div>

      {/* Restart button for failed use cases */}
      {isFailed && (
        <div
          className="mb-4"
          onClick={(e) => e.stopPropagation()} // Prevent card click when interacting with restart button
        >
          <RestartUseCaseButtonComponent useCase={useCase} />
        </div>
      )}
    </div>
  );

  // Conditionally wrap with Link only if clickable
  if (isClickable) {
    return (
      <Link
        href={`/ai-workflows/${useCase.id}`}
        className={cn("block group", className)}
      >
        <CardContent />
      </Link>
    );
  }

  // Return non-clickable card
  return (
    <div className={cn("block", className)}>
      <CardContent />
    </div>
  );
}

// 🔥 FIXED: Compact version also updated
export function PersonalizedUseCaseCardCompactComponent({
  useCase,
  className,
}: PersonalizedUseCaseCardProps) {
  const statusConf = getStatusConfig(useCase.generation_status);
  const StatusIcon = statusConf.icon;
  const isGenerating = statusConf.priority > 1 && statusConf.priority < 9;
  const hasTitle = Boolean(useCase.title);

  // Add the missing isReady variable
  const isCompleted =
    useCase.generation_status === "completed" ||
    useCase.generation_status === "generated";
  const isReady = isCompleted && hasTitle;

  return (
    <Link
      href={`/ai-workflows/${useCase.id}`}
      className={cn("block group", className)}
    >
      <div
        className={cn(
          "bg-beige-light rounded-lg p-4 transition-all duration-200",
          "hover:shadow-md",
          "group-hover:bg-gray-50/30",
        )}
      >
        <div className="flex items-center justify-between mb-2.5">
          <Badge
            variant="outline"
            className={cn(
              "text-xs py-0.5 px-2 rounded-full flex items-center gap-1",
              statusConf.bgColor,
              statusConf.textColor,
              statusConf.borderColor,
            )}
          >
            <StatusIcon
              size={12}
              className={cn(isGenerating && "animate-spin")}
            />
            {statusConf.label}
          </Badge>
          <span className="text-xs text-gray-500">
            {new Date(useCase.created_at || "").toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>

        {useCase.generation_status !== "failed" && (
          <h4
            className={cn(
              "font-medium mb-1.5 line-clamp-1",
              hasTitle ? "text-gray-800" : "text-gray-500 italic",
            )}
          >
            {hasTitle
              ? useCase.title
              : isGenerating
                ? "Generating..."
                : "Use Case"}
          </h4>
        )}

        {useCase.user_input_problem_statement && (
          <p className="text-xs text-gray-600 line-clamp-2">
            {useCase.user_input_problem_statement}
          </p>
        )}
      </div>
    </Link>
  );
}
